import { Box, Button } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PaloConfirmationDialog } from '@synergy/components-palo';
import { ReactElement, ReactNode, useMemo, useState } from 'react';

/** What the list hands over: one row's id and whether it is locked today. */
export interface PaloSelectableRow {
    id: number;
    isLocked?: boolean;
}

export interface PaloListSelectionState {
    /** Ready to hand to PaloListView. */
    selection: {
        selectedIds: number[];
        allSelected: boolean;
        someSelected: boolean;
        onToggle: (id: number) => void;
        onToggleAll: () => void;
    };
    /** The bar above the table, or null while nothing is picked. */
    actions: ReactNode;
    /** Drop the selection — after a bulk run, or when the page changes. */
    clear: () => void;
}

/**
 * Picking several records and locking them in one go.
 *
 * The three master data lists had no selection at all, and the concept asks
 * for the same one everywhere (#2212, LST-10/11). Locking is the "Status"
 * action of LST-11 for a master record; deleting stays a single decision per
 * record and is not offered here.
 *
 * Each record is locked on its own, because that is the only endpoint there
 * is. So the run can half-succeed, and it says so rather than claiming all or
 * nothing — the same way the invoices report their batch (#2136).
 */
export const usePaloListSelection = (
    rows: PaloSelectableRow[],
    lock: (id: number) => Promise<boolean>,
    unlock: (id: number) => Promise<boolean>,
    onDone: () => void
): PaloListSelectionState => {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [asking, setAsking] = useState<'lock' | 'unlock' | undefined>(undefined);
    const [running, setRunning] = useState(false);

    const pageIds = useMemo(() => rows.map(row => row.id), [rows]);
    const onPage = selectedIds.filter(id => pageIds.includes(id));
    const picked = rows.filter(row => selectedIds.includes(row.id));
    const lockable = picked.filter(row => !row.isLocked).length;
    const unlockable = picked.length - lockable;

    const clear = (): void => setSelectedIds([]);

    const run = async (kind: 'lock' | 'unlock'): Promise<void> => {
        const ids = picked.filter(row => ('lock' === kind ? !row.isLocked : row.isLocked)).map(row => row.id);

        setRunning(true);
        setAsking(undefined);

        // Sequential on purpose: forty parallel writes on the same account
        // table is how one turns a bulk action into a lock wait.
        for (const id of ids) {
            await ('lock' === kind ? lock(id) : unlock(id));
        }

        setRunning(false);
        clear();
        onDone();
    };

    const actions = (
        <>
            <Box sx={{ display: 'flex', gap: '8px' }}>
                <Button
                    variant="contained"
                    size="small"
                    disabled={running || 0 === lockable}
                    onClick={() => setAsking('lock')}
                >
                    <FormattedMessage id="app.label.lock" />
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    disabled={running || 0 === unlockable}
                    onClick={() => setAsking('unlock')}
                >
                    <FormattedMessage id="app.label.unlock" />
                </Button>
            </Box>
            <PaloConfirmationDialog
                isVisible={undefined !== asking}
                title={<FormattedMessage id={'lock' === asking ? 'app.label.lock' : 'app.label.unlock'} />}
                action={<FormattedMessage id={'lock' === asking ? 'app.label.lock' : 'app.label.unlock'} />}
                onCancel={() => setAsking(undefined)}
                onConfirm={() => asking && run(asking)}
            >
                <FormattedMessage
                    id={'lock' === asking ? 'app.list.bulk.lock.text' : 'app.list.bulk.unlock.text'}
                    values={{ count: 'lock' === asking ? lockable : unlockable }}
                />
            </PaloConfirmationDialog>
        </>
    );

    return {
        selection: {
            selectedIds,
            allSelected: pageIds.length > 0 && onPage.length === pageIds.length,
            someSelected: onPage.length > 0,
            onToggle: id =>
                setSelectedIds(prev => (prev.includes(id) ? prev.filter(entry => entry !== id) : [...prev, id])),
            onToggleAll: () =>
                setSelectedIds(prev =>
                    pageIds.every(id => prev.includes(id))
                        ? prev.filter(id => !pageIds.includes(id))
                        : [...new Set([...prev, ...pageIds])]
                ),
        },
        actions,
        clear,
    };
};
